import { useQuery } from '@tanstack/react-query';

import { billingApi } from '@/api/services/billing.api';

import { BILLING_QUERY_KEYS } from '../query-keys';

interface IUseBillingUsageOptions {
    /**
     * Skip the request when the caller lacks `billing:read` — the endpoint 403s
     * without it, so app-shell consumers (global banner, call widget) pass this
     * to avoid a per-page failing request for voice-only / agent roles.
     * Defaults to true so existing billing-page callers are unaffected.
     */
    readonly enabled?: boolean;
}

/**
 * LIVE usage summary — the single snapshot behind every plan-limit surface
 * (app-shell banner, per-module usage chips, billing meters). Short staleTime
 * so meters track consumption; mutations that move metered capacity refresh
 * it via the app `mutationCache` (`App.tsx`), keyed on `REFRESHES_PLAN_USAGE`.
 */
export function useBillingUsage(options?: IUseBillingUsageOptions) {
    return useQuery({
        queryKey: BILLING_QUERY_KEYS.usage(),
        queryFn: billingApi.getUsage,
        enabled: options?.enabled ?? true,
        staleTime: 15_000,
        // Opted in against the app-wide `refetchOnWindowFocus: false`: the
        // banner sits in the persistent layout and never remounts, and it is
        // the ONLY observer of this query on pages that render no usage chip.
        // Without this, returning to an already-open tab keeps a "Resource
        // limit reached" warning up after an upgrade (billing page, another
        // tab, or Stripe's hosted checkout) has already raised the limit.
        refetchOnWindowFocus: true,
        // The live endpoint 503s while the billing runtime is down — fail
        // fast into the "temporarily unavailable" alert instead of retrying.
        retry: 1,
    });
}
